'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/lib/i18n';

const buttonVariants = cva(
  // buttons.* theme tokens (each with today's literal as fallback) replace the
  // literal utilities; size variants keep dev-mobile's 44 px mobile floors.
  // Font weight and transition duration come from the [data-slot="button"]
  // rule in globals.css so a caller's font-semibold / duration-* still wins.
  `inline-flex items-center justify-center gap-[var(--pd-button-icon-gap,0.5rem)] whitespace-nowrap rounded-[var(--pd-button-radius,var(--radius))]
   text-[length:var(--pd-button-size,0.875rem)] leading-5 font-[family-name:var(--pd-font-button,inherit)]
   tracking-[var(--pd-button-letter-spacing,0)] [text-transform:var(--pd-button-transform,none)]
   transition-all ease-out
   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
   disabled:pointer-events-none disabled:opacity-50
   active:scale-[0.98]
   [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0`,
  {
    variants: {
      variant: {
        default: `bg-[var(--pd-color-button,hsl(var(--primary)))] text-[color:var(--pd-color-button-foreground,hsl(var(--primary-foreground)))] shadow-[var(--pd-shadow-button,0_1px_3px_0_rgb(0_0_0/0.1),0_1px_2px_-1px_rgb(0_0_0/0.1))]
                  hover:bg-[var(--pd-color-button-hover,hsl(var(--primary)/0.9))] hover:text-[color:var(--pd-color-button-hover-foreground,hsl(var(--primary-foreground)))] hover:shadow-md`,
        destructive: `bg-destructive text-destructive-foreground shadow-[var(--pd-shadow-button,0_1px_3px_0_rgb(0_0_0/0.1),0_1px_2px_-1px_rgb(0_0_0/0.1))]
                      hover:bg-destructive/90 hover:shadow-md`,
        outline: `border-[length:var(--pd-button-border-width,1px)] border-input bg-background shadow-[var(--pd-shadow-button,0_1px_3px_0_rgb(0_0_0/0.1),0_1px_2px_-1px_rgb(0_0_0/0.1))]
                  hover:bg-accent hover:text-accent-foreground`,
        secondary: `bg-secondary text-secondary-foreground shadow-[var(--pd-shadow-button,0_1px_3px_0_rgb(0_0_0/0.1),0_1px_2px_-1px_rgb(0_0_0/0.1))]
                    hover:bg-secondary/80`,
        ghost: `hover:bg-accent hover:text-accent-foreground`,
        link: `text-primary-text underline-offset-4 hover:underline`,
        success: `bg-success text-success-foreground shadow-[var(--pd-shadow-button,0_1px_3px_0_rgb(0_0_0/0.1),0_1px_2px_-1px_rgb(0_0_0/0.1))]
                  hover:bg-success/90 hover:shadow-md`,
      },
      size: {
        // Mobile (< md) floors every size at the 44 px touch minimum and keeps tap
        // labels >= 14 px; desktop values are unchanged.
        default: 'h-11 px-[var(--pd-button-padding-x,1rem)] py-[var(--pd-button-padding-y,0.5rem)] md:h-[var(--pd-button-min-height,2.5rem)]',
        sm: 'h-11 px-[var(--pd-button-padding-x-sm,0.75rem)] text-[length:var(--pd-button-size,0.875rem)] md:h-[var(--pd-button-height-sm,2.25rem)] md:text-[length:var(--pd-font-size-xs,0.75rem)] md:leading-4',
        lg: 'h-[var(--pd-button-height-lg,2.75rem)] px-[var(--pd-button-padding-x-lg,2rem)] text-[length:var(--pd-button-size-lg,1rem)] leading-6',
        xl: 'h-[var(--pd-button-height-xl,3rem)] px-[var(--pd-button-padding-x-xl,2.5rem)] text-[length:var(--pd-button-size-lg,1rem)] leading-6',
        icon: 'h-11 w-11 md:h-[var(--pd-button-min-height,2.5rem)] md:w-[var(--pd-button-min-height,2.5rem)]',
        'icon-sm': 'h-11 w-11 md:h-8 md:w-8',
        'icon-lg': 'h-[var(--pd-button-height-xl,3rem)] w-[var(--pd-button-height-xl,3rem)]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading, children, disabled, ...props }, ref) => {
    const t = useTranslations('common');
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            {t('loading')}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
